// Odczyt pól faktury z tekstu PDF (wynik `pdftotext -layout`).
// Zasada: pole wypełniamy tylko przy jednym spójnym kandydacie – niepewne zostaje puste.
import { parseAmount } from './format.ts';
import type { Invoice, Service } from './types.ts';

export interface Extracted {
  number?: string;
  issueDate?: string;
  paidDate?: string;
  periodFrom?: string;
  periodTo?: string;
  currency?: string;
  net?: number;
  taxCharged?: number;
  vendor?: string;
  vendorVatId?: string;
  country?: string;
}

/** Własne numery VAT z NIP-u (z prefiksem PL i bez) – nie mogą zostać uznane za numer dostawcy. */
export const ownVatIds = (nip: string): string[] => {
  const d = String(nip || '').replace(/\D/g, '');
  return d ? [`PL${d}`, d] : [];
};

export const isEmptyExtracted = (ex: Extracted) => Object.values(ex).every((v) => v === undefined);

// ---------- daty ----------

const MONTHS: Record<string, number> = {};
const addMonths = (names: string[]) => names.forEach((n, i) => n.split('|').forEach((x) => (MONTHS[x] = i + 1)));
addMonths(['january|jan', 'february|feb', 'march|mar', 'april|apr', 'may', 'june|jun', 'july|jul', 'august|aug', 'september|sep|sept', 'october|oct', 'november|nov', 'december|dec']);
addMonths(['januar|jänner', 'februar', 'märz|maerz', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober|okt', 'november', 'dezember|dez']);
addMonths(['stycznia|styczeń|styczen|sty', 'lutego|luty|lut', 'marca|marzec', 'kwietnia|kwiecień|kwi', 'maja|maj', 'czerwca|czerwiec|cze', 'lipca|lipiec|lip', 'sierpnia|sierpień|sie', 'września|wrzesień|wrz', 'października|październik|paź|paz', 'listopada|listopad|lis', 'grudnia|grudzień|gru']);

const monthOf = (name: string) => MONTHS[name.toLowerCase().replace(/\.$/, '')];

function iso(y: number, m: number | undefined, d: number): string | null {
  if (!m || m < 1 || m > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const L = '[A-Za-zÀ-žąćęłńóśźżĄĆĘŁŃÓŚŹŻ]';
const DATE_PATTERNS: { re: RegExp; to: (m: RegExpExecArray) => string | null }[] = [
  { re: /\b(\d{4})-(\d{2})-(\d{2})\b/g, to: (m) => iso(+m[1], +m[2], +m[3]) },
  { re: /\b(\d{1,2})\.(\d{1,2})\.(\d{4})\b/g, to: (m) => iso(+m[3], +m[2], +m[1]) },
  // Ukośnik bywa d/m albo m/d (USA) – tylko gdy zapis jest jednoznaczny.
  { re: /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, to: (m) => {
    const a = +m[1], b = +m[2];
    if (a === b) return iso(+m[3], a, b);
    if (a > 12 && b <= 12) return iso(+m[3], b, a);
    if (b > 12 && a <= 12) return iso(+m[3], a, b);
    return null;
  } },
  { re: new RegExp(`\\b(\\d{1,2})-(${L}{3,4})-(\\d{4})\\b`, 'g'), to: (m) => iso(+m[3], monthOf(m[2]), +m[1]) },
  { re: new RegExp(`\\b(${L}{3,12})\\.? (\\d{1,2}),? (\\d{4})\\b`, 'g'), to: (m) => iso(+m[3], monthOf(m[1]), +m[2]) },
  { re: new RegExp(`\\b(\\d{1,2})\\.? (${L}{3,12})\\.? (\\d{4})\\b`, 'g'), to: (m) => iso(+m[3], monthOf(m[2]), +m[1]) },
];

/** Wszystkie daty w tekście w kolejności występowania. */
function datesIn(s: string): string[] {
  const found: { at: number; date: string }[] = [];
  for (const p of DATE_PATTERNS) {
    p.re.lastIndex = 0;
    for (let m = p.re.exec(s); m; m = p.re.exec(s)) {
      const date = p.to(m);
      if (date) found.push({ at: m.index, date });
    }
  }
  return found.sort((a, b) => a.at - b.at).map((x) => x.date);
}

// ---------- kwoty i waluty ----------

const CODES = 'USD|EUR|PLN|GBP|CHF|CZK|SEK|NOK|DKK|HUF|CAD|AUD|JPY|CNY';
const SYMBOL: Record<string, string> = { $: 'USD', US$: 'USD', '€': 'EUR', '£': 'GBP', 'zł': 'PLN' };
const NUM = '-?\\d{1,3}(?:[ \\u00a0]\\d{3})+(?:[.,]\\d{1,2})?|-?\\d+(?:[.,]\\d+)*';
const AMOUNT = `(?<![\\w.,/])(?:(${CODES}) ?)?(US\\$|[$€£])? ?(${NUM})(?: ?(${CODES}|zł))?(?![\\w/])`;
const AMOUNT_G = new RegExp(AMOUNT, 'gu');
const AMOUNT_FIRST = new RegExp(AMOUNT, 'u');
const AMOUNT_ONLY = new RegExp(`^\\s*${AMOUNT}\\s*$`, 'u');
/** Koniec kolumny etykiety: ≥2 spacje, za którymi zaczyna się tekst innej kolumny (nie kod waluty). */
const NEXT_COL = new RegExp(`\\s{2,}(?=\\p{L})(?!(?:${CODES})\\b|zł)`, 'u');
/** Najdłuższy analizowany wiersz – dłuższe nie występują w fakturach, a spowalniają wyrażenia regularne. */
const MAX_LINE = 500;

interface Money { value: number; cur?: string }

function money(m: RegExpExecArray | RegExpMatchArray): Money | null {
  const value = parseAmount(m[3].replace(/[ \u00a0]/g, ''));
  if (value == null) return null;
  const cur = m[1] || (m[2] ? SYMBOL[m[2]] : undefined) || (m[4] ? SYMBOL[m[4]] ?? m[4] : undefined);
  return { value, cur };
}

/**
 * Pierwsza kwota tuż za etykietą, w jej kolumnie (bez nawiasów i procentów, np. „(23% on €100.00)”).
 * Liczba bez groszy i bez waluty („Article 196”, „2026”) nie jest kwotą.
 */
function amountAfter(rest: string): Money | null {
  const clean = rest.replace(/\([^)]*\)/g, ' ').replace(/-?\d+(?:[.,]\d+)?\s?%/g, ' ').split(NEXT_COL)[0];
  const m = clean.match(AMOUNT_FIRST);
  if (!m || !(m[1] || m[2] || m[4] || /[.,]\d{2}$/.test(m[3]))) return null;
  return money(m);
}

// ---------- etykiety ----------

const RE = {
  number: /\b(?:invoice\s*(?:number|numbe|no\.?|nr\.?|#)|rechnungs(?:nummer|nr\.?)|numer\s+faktury|faktura\s+(?:nr|numer)|nr\s+faktury)/gi,
  issue: /\b(?:date\s+of\s+issue|invoice\s+date|issue\s+date|date\s+issued|rechnungsdatum|data\s+wystawienia)/gi,
  paid: /\b(?:date\s+paid|paid\s+on|payment\s+date|data\s+zapłaty|zahlungsdatum|bezahlt\s+am)/gi,
  period: /\b(?:service\s+term|service\s+period|billing\s+period|leistungszeitraum|abrechnungszeitraum|okres(?:\s+rozliczeniowy|\s+usługi)?)\b/gi,
  netByCur: /\bnet\s+amount\s*(?=\((?:[A-Z]{3})\))/gi,
  net: /\b(?:subtotal|sub-total|net\s+amount|(?:amount|total)\s+excl(?:uding|\.)?\s+(?:tax|vat)|zwischensumme|nettobetrag|warto[śs][ćc]\s+netto|netto)\b/gi,
  total: /\b(?:grand\s+total|total|amount\s+due|amount\s+paid|gross\s+value|gesamtbetrag|do\s+zapłaty|razem)\b/gi,
  // „VAT No”, „VAT ID(NIP)”, „Tax rate”, „Total excluding tax” itp. to nie kwoty podatku.
  tax: /(?<!\b(?:excl|incl)[a-z]*\.?\s+)\b(?:taxes|tax|vat|mwst|ust\.?|kwota\s+vat|podatek\s+vat)\b(?![\s(]*(?:id\b|no\b|no\.|nr\b|number|reg|code|liab|rate|nip\b|identification))/gi,
  currency: /\b(?:currency|waluta|währung)\b[\s:]*([A-Z]{3})\b/gi,
  // Etykieta sprzedawcy tylko na początku segmentu – „Seller's Country” w zdaniu to nie etykieta.
  seller: /^(?:sold\s+by|seller|sprzedawca|verkäufer|vendor|supplier)\b/i,
  // Wiersze, na których kończy się blok adresu sprzedawcy przy szukaniu w górę.
  blockStop: /^(?:invoice\b|date\b|data\b|rechnung|faktura|bill\s+to|billed\s+to|invoice\s+to|customer|buyer|nabywca|kunde|sold\s+by|seller|sprzedawca)/i,
  buyer: /\b(?:bill\s+to|billed\s+to|invoice\s+to|customer|buyer|nabywca|kunde|rechnungsempfänger)\b/i,
  vatLine: /\b(?:VAT|NIP|USt|TVA|IVA|BTW|Tax\s+ID)\b/i,
  vatId: /(?<![A-Za-z0-9])([A-Z]{2}[A-Z0-9]{8,12})(?![A-Za-z0-9])/g,
};

/** Dla każdego wystąpienia etykiety: tekst za nią w tym wierszu (oraz numer wiersza). */
function afterLabel(lines: string[], re: RegExp): { i: number; rest: string }[] {
  const out: { i: number; rest: string }[] = [];
  lines.forEach((line, i) => {
    re.lastIndex = 0;
    for (let m = re.exec(line); m; m = re.exec(line)) out.push({ i, rest: line.slice(m.index + m[0].length) });
  });
  return out;
}

/** Jedna wartość albo nic – sprzeczni kandydaci znaczą „niepewne”. */
function single<T>(values: (T | null | undefined)[]): T | undefined {
  const set = [...new Set(values.filter((v): v is T => v != null))];
  return set.length === 1 ? set[0] : undefined;
}

function labeledDate(lines: string[], re: RegExp): string | undefined {
  return single(afterLabel(lines, re).map(({ i, rest }) => {
    const own = datesIn(rest)[0];
    if (own) return own;
    // Etykieta w osobnym wierszu (np. „Invoice date” nad „(Data wystawienia): 18.10.2026”).
    if (rest.trim()) return null;
    return datesIn(lines.slice(i + 1, i + 3).join('\n'))[0] ?? null;
  }));
}

function invoiceNumber(lines: string[]): string | undefined {
  return single(afterLabel(lines, RE.number).map(({ rest }) => {
    const tok = rest.replace(/^\s*(?:\([^)]*\))?\s*[:#.：]?\s*/, '').match(/^\S+/)?.[0];
    return tok && /^[A-Za-z0-9][A-Za-z0-9/_.-]{1,62}[A-Za-z0-9]$/.test(tok) && /\d/.test(tok) ? tok : null;
  }));
}

const RANGE = new RegExp(`\\b(${L}{3,9})\\.? (\\d{1,2})(?:, (\\d{4}))? ?[–—-] ?(${L}{3,9})\\.? (\\d{1,2}), (\\d{4})\\b`, 'g');

function period(lines: string[]): { from: string; to: string } | undefined {
  const pairs: string[] = [];
  for (const { rest } of afterLabel(lines, RE.period)) {
    const d = datesIn(rest);
    if (d.length >= 2) pairs.push(`${d[0]}|${d[1]}`);
  }
  // Okres w wierszu pozycji, jak w fakturach Stripe: „Oct 2–Nov 2, 2026”, „Dec 15–Jan 15, 2027”.
  for (const line of lines) {
    RANGE.lastIndex = 0;
    for (let m = RANGE.exec(line); m; m = RANGE.exec(line)) {
      const m1 = monthOf(m[1]), m2 = monthOf(m[4]), y2 = +m[6];
      if (!m1 || !m2) continue;
      const y1 = m[3] ? +m[3] : m1 * 100 + +m[2] > m2 * 100 + +m[5] ? y2 - 1 : y2;
      const from = iso(y1, m1, +m[2]), to = iso(y2, m2, +m[5]);
      if (from && to && from <= to) pairs.push(`${from}|${to}`);
    }
  }
  const p = single(pairs);
  return p ? { from: p.split('|')[0], to: p.split('|')[1] } : undefined;
}

function currencyOf(text: string): string | undefined {
  const codes = new RegExp(`^(?:${CODES})$`);
  const labeled = single([...text.matchAll(RE.currency)].map((m) => (codes.test(m[1]) ? m[1] : null)));
  if (labeled) return labeled;
  const seen = new Set<string>();
  for (const m of text.matchAll(AMOUNT_G)) {
    const x = money(m);
    if (x?.cur) seen.add(x.cur);
  }
  return seen.size === 1 ? [...seen][0] : undefined;
}

/** Kwoty z wierszy z etykietą, w walucie faktury (waluta w nawiasie po etykiecie albo przy kwocie). */
function labeledAmounts(lines: string[], re: RegExp, cur: string | undefined): number[] {
  const out: number[] = [];
  for (const { i, rest } of afterLabel(lines, re)) {
    const parenCur = rest.match(/^\s*\(([A-Z]{3})\)/)?.[1];
    // Kwota w następnym wierszu tylko wtedy, gdy za etykietą nie ma liczb, a wiersz niżej to sama kwota.
    const below = !/\d/.test(rest) ? lines[i + 1]?.match(AMOUNT_ONLY) : null;
    const x = amountAfter(rest) ?? (below ? money(below) : null);
    if (!x) continue;
    const c = parenCur || x.cur;
    if (cur && c && c !== cur) continue;
    out.push(x.value);
  }
  return out;
}

// ---------- sprzedawca ----------

const EU = new Set(['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']);
const COUNTRY: Record<string, string> = {
  'united states': 'US', 'united states of america': 'US', usa: 'US', 'stany zjednoczone': 'US', ireland: 'IE', irlandia: 'IE',
  germany: 'DE', deutschland: 'DE', niemcy: 'DE', netherlands: 'NL', 'the netherlands': 'NL', holandia: 'NL', cyprus: 'CY', cypr: 'CY',
  denmark: 'DK', dania: 'DK', 'united kingdom': 'GB', uk: 'GB', 'wielka brytania': 'GB', france: 'FR', francja: 'FR',
  'czech republic': 'CZ', czechia: 'CZ', czechy: 'CZ', china: 'CN', chiny: 'CN', singapore: 'SG', singapur: 'SG', canada: 'CA', kanada: 'CA',
  australia: 'AU', sweden: 'SE', szwecja: 'SE', luxembourg: 'LU', luksemburg: 'LU', estonia: 'EE', lithuania: 'LT', litwa: 'LT',
  spain: 'ES', hiszpania: 'ES', italy: 'IT', włochy: 'IT', austria: 'AT', belgium: 'BE', belgia: 'BE', switzerland: 'CH', szwajcaria: 'CH',
  israel: 'IL', izrael: 'IL', poland: 'PL', polska: 'PL',
};

interface Seg { start: number; text: string }
const segments = (line: string): Seg[] => [...line.matchAll(/\S+(?: \S+)*/g)].map((m) => ({ start: m.index!, text: m[0] }));
const segAt = (line: string, col: number) => segments(line).find((s) => Math.abs(s.start - col) <= 3);

const looksLikeName = (t: string) => !/^(?:https?:|www\.)|@|:$/.test(t) && !RE.blockStop.test(t) && /\p{L}{2}/u.test(t);
const CLIMB = 8;

function vendorBlock(lines: string[], own: Set<string>): { vendor?: string; vatId?: string; block: string[] } {
  // Numer VAT dostawcy: w segmencie z „VAT/NIP” (albo tuż za nim), nie własny i nie w bloku nabywcy.
  const vats: { i: number; col: number; id: string; top: number }[] = [];
  lines.forEach((line, i) => {
    const segs = segments(line);
    segs.forEach((seg, k) => {
      if (!RE.vatLine.test(seg.text) || RE.buyer.test(seg.text)) return;
      const where = [seg, segs[k + 1]].filter(Boolean) as Seg[];
      for (const w of where) {
        const ids = [...w.text.matchAll(RE.vatId)].map((m) => m[1])
          .filter((id) => (id.match(/\d/g) || []).length >= 6 && !own.has(id) && !own.has(id.slice(2)));
        if (!ids.length) continue;
        // W górę w tej samej kolumnie, do pustego miejsca albo etykiety; blok nabywcy („Bill to”) odpada.
        let top = i, buyer = false;
        while (top > 0 && i - top < CLIMB) {
          const up = segAt(lines[top - 1], seg.start);
          if (!up) break;
          if (RE.buyer.test(up.text)) { buyer = true; break; }
          if (RE.blockStop.test(up.text)) break;
          top--;
        }
        if (!buyer) ids.forEach((id) => vats.push({ i, col: seg.start, id, top }));
        break;
      }
    });
  });
  const ids = [...new Set(vats.map((v) => v.id))];
  const vat = ids.length === 1 ? vats.find((v) => v.id === ids[0]) : undefined;

  // Etykieta „Sold by / Sprzedawca”: nazwa to pierwszy wpis pod nią w tej samej kolumnie.
  for (let li = 0; li < lines.length; li++) {
    const label = segments(lines[li]).find((x) => RE.seller.test(x.text));
    if (!label) continue;
    for (let k = li + 1; k < Math.min(lines.length, li + CLIMB); k++) {
      const s = segAt(lines[k], label.start);
      if (!s || !looksLikeName(s.text)) continue;
      const end = vat && vat.col === label.start ? vat.i : k + 6;
      return { vendor: s.text, vatId: vat?.id, block: lines.slice(k, end + 1).map((l) => segAt(l, label.start)?.text || '') };
    }
    break;
  }
  if (!vat) return { block: [] };
  const block = lines.slice(vat.top, vat.i + 1).map((l) => segAt(l, vat.col)?.text || '');
  const vendor = vat.top < vat.i && looksLikeName(block[0]) ? block[0] : undefined;
  return { vendor, vatId: vat.id, block };
}

function countryOf(vatId: string | undefined, block: string[]): string | undefined {
  const prefix = vatId?.slice(0, 2);
  if (prefix && EU.has(prefix)) return prefix === 'EL' ? 'GR' : prefix;
  return single(block.map((l) => COUNTRY[l.trim().toLowerCase().replace(/[.,]$/, '')]));
}

// ---------- całość ----------

/** Jedna wartość ze źródła; sprzeczne wartości zatrzymują szukanie (nie schodzimy do słabszego źródła). */
function firstSource(sources: (() => number[])[]): number | undefined {
  for (const src of sources) {
    const vals = [...new Set(src())];
    if (vals.length === 1) return vals[0];
    if (vals.length > 1) return undefined;
  }
  return undefined;
}

export function parseInvoiceText(text: string, own: { vatIds: string[] }): Extracted {
  const lines = text.replace(/\r/g, '').split('\n').map((l) => (l.length > MAX_LINE ? l.slice(0, MAX_LINE) : l));
  text = lines.join('\n');
  const ownIds = new Set(own.vatIds.map((v) => v.replace(/\s/g, '').toUpperCase()));
  const ex: Extracted = {};

  ex.number = invoiceNumber(lines);
  ex.issueDate = labeledDate(lines, RE.issue);
  ex.paidDate = labeledDate(lines, RE.paid);
  const p = period(lines);
  if (p) { ex.periodFrom = p.from; ex.periodTo = p.to; }

  ex.currency = currencyOf(text);
  const taxes = [...new Set(labeledAmounts(lines, RE.tax, ex.currency).filter((v) => v > 0))];
  if (taxes.length === 1) ex.taxCharged = taxes[0];
  const positive = (re: RegExp) => () => labeledAmounts(lines, re, ex.currency).filter((v) => v > 0);
  // „Total” jako netto tylko wtedy, gdy na fakturze nie ma żadnego podatku.
  ex.net = firstSource([positive(RE.netByCur), positive(RE.net), ...(taxes.length ? [] : [positive(RE.total)])]);

  const v = vendorBlock(lines, ownIds);
  ex.vendor = v.vendor;
  ex.vendorVatId = v.vatId;
  ex.country = countryOf(v.vatId, v.block);

  // Bez numeru, daty wystawienia i numeru VAT dostawcy to nie faktura (umowa, wyciąg, regulamin) – nic nie wypełniamy.
  if (!ex.number && !ex.issueDate && !ex.vendorVatId) return {};
  for (const k of Object.keys(ex) as (keyof Extracted)[]) if (ex[k] === undefined) delete ex[k];
  return ex;
}

// ---------- wpisywanie do faktury ----------

const LEGAL = /\b(?:ltd|limited|llc|l\.l\.c|inc|incorporated|corp|corporation|co|company|gmbh|ag|bv|b\.v|ab|oy|plc|pbc|sa|s\.a|srl|s\.r\.l|sro|s\.r\.o|sp|z|o\.o|oo|opco)\b/g;
const normName = (s: string | undefined) =>
  String(s || '').toLowerCase().replace(/[.,]/g, ' ').replace(LEGAL, ' ').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const normVat = (s: string | undefined) => String(s || '').replace(/[\s.-]/g, '').toUpperCase();

/** Usługi pasujące do dostawcy z PDF: po numerze VAT, a gdy żadna – po nazwie firmy. */
export function matchServices(ex: Extracted, services: Record<string, Service>): string[] {
  const active = Object.entries(services).filter(([, s]) => s.active !== false);
  const vat = normVat(ex.vendorVatId);
  if (vat) {
    const byVat = active.filter(([, s]) => s.vendorVat && normVat(s.vendorVat) === vat);
    if (byVat.length) return byVat.map(([id]) => id);
  }
  const name = ` ${normName(ex.vendor)} `;
  if (name.trim().length < 3) return [];
  return active.filter(([, s]) => {
    const v = normName(s.vendor);
    return v.length >= 3 && (name.includes(` ${v} `) || ` ${v} `.includes(name));
  }).map(([id]) => id);
}

/** Jedna pasująca usługa albo null (brak albo kilka – wtedy wybiera użytkownik). */
export function matchService(ex: Extracted, services: Record<string, Service>): string | null {
  const ids = matchServices(ex, services);
  return ids.length === 1 ? ids[0] : null;
}

const isBlank = (v: unknown) => v == null || v === '';

/**
 * Wpisuje odczytane pola tylko tam, gdzie faktura jest pusta (albo pole jest w `ctx.replace`). Data zapłaty: z PDF, potem z potwierdzenia
 * zapłaty (ctx.paidDate), a przy usłudze „płacę kartą w dniu faktury” – data wystawienia.
 */
export function applyExtracted(
  inv: Invoice, ex: Extracted, ctx: { services: Record<string, Service>; paidDate?: string; replace?: string[] },
): { inv: Invoice; filled: string[] } {
  const svc = inv.serviceId ? ctx.services[inv.serviceId] : undefined;
  const next: Invoice = { ...inv };
  const filled: string[] = [];
  // `replace`: pola z wartościami zastępczymi (np. data maila w szkicu z Gmaila) – dane z PDF mają pierwszeństwo.
  const put = <K extends keyof Invoice>(k: K, v: Invoice[K] | undefined) => {
    if (v == null || v === '' || v === next[k] || !(isBlank(next[k]) || ctx.replace?.includes(k))) return;
    next[k] = v;
    filled.push(k);
  };
  put('number', ex.number);
  put('issueDate', ex.issueDate);
  put('paidDate', ex.paidDate ?? ctx.paidDate ?? (svc?.cardAutopay ? ex.issueDate || next.issueDate : undefined));
  put('periodFrom', ex.periodFrom);
  put('periodTo', ex.periodTo);
  put('currency', ex.currency);
  put('amount', ex.net);
  put('vatCharged', ex.taxCharged);
  if (!inv.serviceId) {
    put('vendor', ex.vendor);
    put('country', ex.country);
  }
  next.extracted = filled;
  return { inv: next, filled };
}

/**
 * Szkic faktury z pliku (import z Gmaila i dodawanie z plików): dane z PDF, a puste pola z wartości zastępczych –
 * data wystawienia `ctx.today`, waluta z usługi. Lista pól z PDF znika, gdy nic nie odczytano.
 */
export function draftInvoice(base: Invoice, ex: Extracted | null, ctx: { services: Record<string, Service>; paidDate?: string; today: string }): Invoice {
  const inv: Invoice = ex ? applyExtracted(base, ex, { services: ctx.services, paidDate: ctx.paidDate }).inv : { ...base };
  const svc = inv.serviceId ? ctx.services[inv.serviceId] : undefined;
  if (!inv.issueDate) inv.issueDate = ctx.today;
  if (!inv.currency) inv.currency = svc?.currency || '';
  if (!inv.extracted?.length) delete inv.extracted;
  return inv;
}

const normNumber = (s: string | undefined) => String(s || '').replace(/\s+/g, '').toLowerCase();

/** Faktura o tym samym numerze od tej samej usługi (albo – dla „Inne” – tego samego dostawcy). */
export function findDuplicateInvoice(invoices: Record<string, Invoice>, inv: Invoice): string | null {
  const num = normNumber(inv.number);
  if (!num) return null;
  const vendor = normName(inv.vendor);
  for (const [id, x] of Object.entries(invoices)) {
    if (normNumber(x.number) !== num) continue;
    if (inv.serviceId ? x.serviceId === inv.serviceId : !x.serviceId && !!vendor && normName(x.vendor) === vendor) return id;
  }
  return null;
}

/** Wynik dodania faktury z pliku. */
export type InvoiceImportResult =
  | { status: 'created'; invoiceId: string; filled: string[]; message?: string }
  | { status: 'duplicate'; invoiceId: string; message: string };

